import React, { memo } from 'react'

import {
  BrushAI, Button, CubeAIOutline, DeleteOutlined, Download, Icon, Tooltip, VisibilityOff,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import {
  bool, func, number, oneOfType, string,
} from 'prop-types'

import { iconSize } from 'Pods/LazyLoaded/ImageGalleryPanel/constants'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'

import { NOOP } from 'Utils/stableDefaults'

const editIcon = <Icon svgComponent={BrushAI} size={iconSize} color="darker" notSpaced />
const downloadIcon = <Icon svgComponent={Download} size={iconSize} color="darker" notSpaced />
const hideIcon = <Icon svgComponent={VisibilityOff} size={iconSize} color="darker" notSpaced />
const deleteIcon = <Icon svgComponent={DeleteOutlined} size={iconSize} color="darker" notSpaced />
const generateIcon = <Icon svgComponent={CubeAIOutline} size={iconSize} color="light" notSpaced />

// Hover actions of an image card. Admins also get the moderation pair (hide as NSFW,
// delete) in the top-right corner; it is per-user state and so only mounts after pageConfig.
function Actions({
  id,
  title,
  isAdmin = false,
  onEdit = NOOP,
  onDownload = NOOP,
  onGenerate3d = NOOP,
  onHide = NOOP,
  onDelete = NOOP,
}) {
  const t = useTranslation()

  return (
    <>
      <div className="image-gallery-card__actions">
        <Button
          type="secondary"
          size="tiny"
          variant="only-icon"
          actionType="button"
          icon={editIcon}
          customClass="image-gallery-card__action"
          onClick={onEdit}
          aria-label={t('imageGallery.card.edit', { title })}
        />
        <Button
          type="secondary"
          size="tiny"
          variant="only-icon"
          actionType="button"
          icon={downloadIcon}
          customClass="image-gallery-card__action"
          onClick={onDownload}
          aria-label={t('imageGallery.card.download', { title })}
        />
      </div>
      {isAdmin && (
        <div className="image-gallery-card__admin-actions">
          <Tooltip
            id={`image-gallery-card-${id}-hide-tooltip`}
            place="bottom"
            content={t('imageGallery.moderation.hide.confirm')}
          >
            <Button
              type="secondary"
              size="tiny"
              variant="only-icon"
              actionType="button"
              icon={hideIcon}
              customClass="image-gallery-card__action"
              onClick={onHide}
              aria-label={t('imageGallery.card.hide', { title })}
            />
          </Tooltip>
          <Tooltip
            id={`image-gallery-card-${id}-delete-tooltip`}
            place="bottom"
            content={t('imageGallery.moderation.delete.confirm')}
          >
            <Button
              type="secondary"
              size="tiny"
              variant="only-icon"
              actionType="button"
              icon={deleteIcon}
              customClass="image-gallery-card__action"
              onClick={onDelete}
              aria-label={t('imageGallery.card.delete', { title })}
            />
          </Tooltip>
        </div>
      )}
      <Button
        type="ai-primary"
        size="tiny"
        variant="with-icon"
        actionType="button"
        icon={generateIcon}
        customClass="image-gallery-card__generate"
        onClick={onGenerate3d}
        aria-label={t('imageGallery.card.generate3dFor', { title })}
      >
        {t('imageGallery.card.generate3d')}
      </Button>
    </>
  )
}

Actions.propTypes = {
  id: oneOfType([number, string]).isRequired,
  title: string.isRequired,
  isAdmin: bool,
  onEdit: func,
  onDownload: func,
  onGenerate3d: func,
  onHide: func,
  onDelete: func,
}

export default memo(Actions)
